import { useChat } from '@ai-sdk/react';
import { Column, Columns } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { config } from 'lib-shared-backend-config';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { useAppSelector } from 'utils/redux/useAppSelector';
import { useSidekickStarters } from 'utils/sidekickStarters';

import { usePanelGeometry } from '../../hooks/usePanelGeometry';
import {
  createSubagentMcpClient,
  type Subagent,
  type SubagentMcpClient,
} from '../../utils/sdk/mcpClient';
import { type MimUIMessage } from '../../utils/sdk/structuredResponse';
import {
  createSubagentTool,
  type SubagentHistories,
} from '../../utils/sdk/subagents';
import { createBrowserChatTransport } from '../../utils/sdk/transport';

import { MimSidekickComposer } from './components/mimSidekickComposer/mimSidekickComposer';
import { MimSidekickHeader } from './components/mimSidekickHeader/mimSidekickHeader';
import { MimSidekickMessageList } from './components/mimSidekickMessageList/mimSidekickMessageList';
import { MimSidekickResizeHandles } from './components/mimSidekickResizeHandles/mimSidekickResizeHandles';
import styles from './mimSidekickWidget.module.scss';

export type MimSidekickMode = 'floating' | 'docked';

type MimSidekickProps = {
  className: string;
  onClose: () => void;
};

type SubagentConfig = {
  mainPrompt: string;
  subagents: Subagent[];
  hasError: boolean;
};

const DEFAULT_MAIN_PROMPT = 'chat-main-agent';

const EMPTY_SUBAGENT_CONFIG: SubagentConfig = {
  mainPrompt: DEFAULT_MAIN_PROMPT,
  subagents: [],
  hasError: false,
};

/**
 * Mim sidekick with browser-side agent loop. The main agent orchestrates the
 * loop and delegates server-side tool calls to subagents. See
 * https://ai-sdk.dev/docs/agents/subagents#specialized-orchestration for
 * details.
 */
export const MimSidekickWidget = ({
  className,
  onClose,
}: MimSidekickProps): JSX.Element | null => {
  const [chatId] = useState(() => uuid().replace(/-/g, ''));

  const organizationId = useAppSelector(
    (state) => state.settings.organization.organization.id,
  );

  const [subagentConfig, setSubagentConfig] = useState<SubagentConfig>(
    EMPTY_SUBAGENT_CONFIG,
  );

  const [mode, setMode] = useState<MimSidekickMode>('docked');

  useEffect(() => {
    let clients: SubagentMcpClient | null = null;
    let isCancelled = false;

    void (async () => {
      const next = await createSubagentMcpClient(config.url.rest);
      if (isCancelled) {
        await next.dispose();
        return;
      }
      clients = next;
      setSubagentConfig({
        mainPrompt: next.mainPrompt,
        subagents: next.subagents,
        hasError: next.hasError,
      });
    })();

    return () => {
      isCancelled = true;
      if (clients) void clients.dispose();
    };
  }, []);

  const conversationId = useAppSelector(
    (state) => state.conversation.metaConversation?.conversation.id,
  );

  const subagentsRef = useRef(subagentConfig.subagents);
  subagentsRef.current = subagentConfig.subagents;

  const mainPromptRef = useRef(subagentConfig.mainPrompt);
  mainPromptRef.current = subagentConfig.mainPrompt;

  const starters = useSidekickStarters();
  const { pathname } = useLocation();

  // Search params are left out to keep filter state out of the prompt.
  const contextRef = useRef('');
  contextRef.current = [
    `The user is on the page ${pathname}. Use it as context when relevant to the question.`,
    conversationId
      ? `The agent is currently viewing the Dixa conversation with id ${conversationId}. Use it as context when relevant to the question.`
      : '',
  ]
    .filter(Boolean)
    .join('\n');

  const subagentHistories = useMemo<SubagentHistories>(
    () => new Map(),
    [chatId],
  );

  const transport = useMemo(
    () =>
      createBrowserChatTransport(
        (writer) =>
          Object.fromEntries(
            subagentsRef.current.map((subagent) => [
              subagent.name,
              createSubagentTool(subagent, chatId, writer, subagentHistories),
            ]),
          ),
        () => mainPromptRef.current,
        () => contextRef.current,
      ),
    [chatId, subagentHistories],
  );

  const { messages, sendMessage, status } = useChat<MimUIMessage>({
    id: chatId,
    transport,
    experimental_throttle: 50,
  });

  const { panelRef, panelStyle, startDrag, startResize, isInteracting } =
    usePanelGeometry(mode === 'floating');

  if (!organizationId) return null;

  const isDocked = mode === 'docked';

  return (
    <aside
      ref={panelRef}
      className={classnames(
        className,
        isDocked ? styles.docked : styles.drawer,
        { [styles.interacting]: isInteracting },
      )}
      style={isDocked ? undefined : panelStyle}
    >
      <Columns>
        <Column>
          <MimSidekickHeader
            onClose={onClose}
            mode={mode}
            onToggleMode={() =>
              setMode((prev) => (prev === 'floating' ? 'docked' : 'floating'))
            }
            dragHandleProps={
              isDocked
                ? undefined
                : {
                    onPointerDown: startDrag,
                    className: styles.dragHandle,
                  }
            }
          />
          <MimSidekickMessageList
            messages={messages}
            starters={starters}
            onSelectChoice={(text) => sendMessage({ text })}
          />
          <Column.Row
            borderTop
            noVerticalPadding
            noHorizontalPadding
            noMinHeight
          >
            <MimSidekickComposer
              disabled={status !== 'ready'}
              onSend={(text) => sendMessage({ text })}
            />
          </Column.Row>
        </Column>
      </Columns>
      {!isDocked && <MimSidekickResizeHandles onResizeStart={startResize} />}
    </aside>
  );
};
